<template>
  <div :class="['search-filter', 'search-filter-boolean', { 'is-expanded': expanded }]">
    <div class="filter-header">
      <b-button class="filter-expander" variant="link" block @click="toggleExpanded()">
        <b-icon icon="arrow-dropdown" scale="0.9" />{{ filter.displayName }} <span v-if="filterSetCount" class="filter-set-count">{{ filterSetCount }}</span>
      </b-button>
    </div>

    <transition-expand>
      <div v-if="expanded" class="filter-body">
        <div class="filter-controls">
          <div
               v-for="control in filter.controls"
               :key="control.id"
               v-show="!control.hide"
               class="controls-control">
            <div class="control-tristate">
              <b-button
                        :id="`${control.id}-false`"
                        variant="danger"
                        size="sm"
                        class="control-button control-button-false"
                        :class="{ 'active': control.value === false, 'inactive': control.value !== false }"
                        @click="setValue(control, false)">
                <b-icon icon="close" />
              </b-button>

              <div
                   class="control-toggle-switch"
                   :class="getToggleSwitchClass(control.value)"
                   @click="cycleValue(control)">
                <div class="toggle-track">
                  <div class="toggle-thumb" :class="getToggleThumbClass(control.value)"></div>
                </div>
              </div>

              <b-button
                        :id="`${control.id}-true`"
                        variant="success"
                        size="sm"
                        class="control-button control-button-true"
                        :class="{ 'active': control.value === true, 'inactive': control.value !== true }"
                        @click="setValue(control, true)">
                <b-icon icon="check" />
              </b-button>
            </div>

            <b-button variant="link" size="sm" class="control-reset" :disabled="noneFilterSet" @click="resetControls"><b-icon icon="close" scale="0.8" /></b-button>
          </div>
        </div>
      </div>
    </transition-expand>
  </div>
</template>

<script>
import TransitionExpand from '@/components/extension/TransitionExpand';
import MixinFilterBase from '@/components/search/MixinFilterBase';

export default {
  name: 'Search.FilterBoolean',
  mixins: [MixinFilterBase],
  components: {
    TransitionExpand
  },
  methods: {
    setValue(control, value) {
      control.value = value
      this.updateControl(control, control.value)
    },
    cycleValue(control) {
      // Cycle through: null → false → true → null
      if (control.value === null || control.value === undefined) {
        control.value = false
      } else if (control.value === false) {
        control.value = true
      } else {
        control.value = null
      }
      this.updateControl(control, control.value)
    },
    getToggleSwitchClass(value) {
      if (value === true) {
        return 'state-true'
      } else if (value === false) {
        return 'state-false'
      }
      return 'state-null'
    },
    getToggleThumbClass(value) {
      if (value === true) {
        return 'thumb-right'
      } else if (value === false) {
        return 'thumb-left'
      }
      return 'thumb-center'
    }
  }
}
</script>

<style lang="scss">
$filterboolean-controls-control-toggle-padding-x: $input-padding-x-sm !default;

$filterboolean-control-reset-color: $danger !default;
$filterboolean-control-reset-color-disabled: $btn-link-disabled-color !default;

$filterboolean-button-size: 1rem !default;
$filterboolean-toggle-width: 2.75rem !default;
$filterboolean-toggle-height: 1.125rem !default;
$filterboolean-toggle-thumb-size: 0.875rem !default;
$filterboolean-toggle-gap: 0.375rem !default;

.search-filter-boolean {
  .filter-controls {
    .controls-control {
      display: flex;
      justify-content: space-between;
      align-items: center;
      position: relative;

      .control-tristate {
        display: flex;
        align-items: center;
        gap: $filterboolean-toggle-gap;
        flex: 1;
        position: relative;
        z-index: 1;
      }

      .control-button {
        width: $filterboolean-button-size;
        height: $filterboolean-button-size;
        padding: 0;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;
        border: none;

        .bi {
          font-size: 0.75rem;
          color: $white;
          display: block;
        }

        &.inactive {
          opacity: 0.5;
        }

        &.active {
          opacity: 1;
        }
      }

      .control-toggle-switch {
        flex: 0 0 auto;
        cursor: pointer;
        user-select: none;

        .toggle-track {
          width: $filterboolean-toggle-width;
          height: $filterboolean-toggle-height;
          background-color: $white;
          border: 1px solid $gray-300;
          border-radius: $filterboolean-toggle-height;
          position: relative;
          transition: background-color 0.2s ease;

          .toggle-thumb {
            width: $filterboolean-toggle-thumb-size;
            height: $filterboolean-toggle-thumb-size;
            background-color: $gray-500;
            border-radius: 50%;
            position: absolute;
            top: 50%;
            transform: translateY(-50%);
            transition: left 0.2s ease, background-color 0.2s ease;
            box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
          }

          .thumb-left {
            left: 0.125rem;
          }

          .thumb-center {
            left: 50%;
            transform: translate(-50%, -50%);
          }

          .thumb-right {
            right: 0.125rem;
            left: auto;
          }
        }

        &.state-false .toggle-track {
          background-color: rgba($danger, 0.1);
          border-color: $danger;

          .toggle-thumb {
            background-color: $danger;
          }
        }

        &.state-true .toggle-track {
          background-color: rgba($success, 0.1);
          border-color: $success;

          .toggle-thumb {
            background-color: $success;
          }
        }

        &.state-null .toggle-track {
          background-color: $white;
          border-color: $gray-300;

          .toggle-thumb {
            background-color: $gray-500;
          }
        }
      }

      .control-reset {
        display: block;
        position: absolute;
        top: 0;
        bottom: 0;
        right: $filterboolean-controls-control-toggle-padding-x;
        padding: 0;
        width: 1.5em;
        color: $filterboolean-control-reset-color;
        box-shadow: none;
        z-index: 2;

        &.disabled {
          color: $filterboolean-control-reset-color-disabled;
        }
      }
    }
  }
}
</style>
